// @TODO: fix this accessibility issue
/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */

import InputLabel from "@flick/front_end_patterns/src/js/forms/form_fields_label";
import FormValidator from "@flick/front_end_patterns/src/js/forms/form_validator";
import { PRODUCT_LABELS, ROUTES } from "domain/configuration";
import {
  checkSuccessfulAuthorisationAction,
  createWindcaveTransactionAction,
  updateFlickSignUpAction,
} from "domain/core/actions";
import {
  setCurrentFormStep,
  setFlickSignUpStateComplete,
  setLeadSource,
  setPromoCode,
  setSignUpPaymentType,
  setUserPayment,
} from "domain/core/reducers";
import {
  ApiPaymentTypes,
  BankAccountType,
  BillingPeriod,
  SignUpFormPaymentTypes,
} from "domain/core/types";
import { rollbar } from "lib/api";
import { restartForm } from "lib/hooks";
import { REQUEST_STATUS } from "lib/types";
import {
  ALPHANUMERIC_PATTERN,
  DD_ACCOUNT_NAME_REGEX_PATTERN,
  fetchWindcaveTransactionId,
  PROMO_CODE_REGEX_PATTERN,
  sendAnalyticsCustomEvent,
  sendAnalyticsPageEvent,
  sendAnalyticsPageView,
} from "lib/util";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router";
import { useAppDispatch, useAppSelector } from "store/storeHooks";
import { Prompt } from "ui/components/Prompt";

interface State {
  signUpPaymentType?: SignUpFormPaymentTypes;
  billingPeriod?: BillingPeriod;
  accName: string;
  accNumber: string;
  statementParticulars: string;
  promoCode?: string;
  statementCode: string;
  authorityCheck: boolean;
  directDebitTnCCheck: boolean;
  updateViaSMSCheck: boolean;
  leadSource?: string;
  errCreditCard?: string;
  errSubmission?: string;
  navigatingToCreditCard: boolean;
  signUpComplete: boolean;
}

export function Payment() {
  const dispatch = useAppDispatch();
  const {
    selectedAddress: address,
    user: { details, payment, checks: userChecks, property },
    windcaveAuthorisationRequestStatus,
    updateFlickSignUpRequestStatus: updateSignUpStatus,
    flickAccountError: error,
    promoCode,
    leadSource,
    signUpPaymentType,
    estimatedUsageType,
    selectedEligibilityType,
    flickUserId,
  } = useAppSelector((store) => store.coreData);

  const navigate = useNavigate();
  const location = useLocation();
  const formRef = useRef(null);

  // catch all fix for missing address
  useEffect(() => {
    if (flickUserId && !Boolean(address)) {
      restartForm(dispatch, navigate);
    }
  }, [flickUserId]);

  const [state, setState] = useState<State>({
    signUpPaymentType: signUpPaymentType,
    accName: "",
    accNumber: "",
    statementParticulars: "",
    statementCode: "",
    promoCode: promoCode || "",
    authorityCheck: false,
    directDebitTnCCheck: false,
    updateViaSMSCheck: true,
    billingPeriod: payment.billingPeriod,
    leadSource: leadSource,
    errSubmission: undefined,
    errCreditCard: "",
    navigatingToCreditCard: false,
    signUpComplete: false,
  });

  useEffect(() => {
    sendAnalyticsPageView(ROUTES.payment);
  }, []);

  useEffect(() => {
    new InputLabel().start();
    new FormValidator().start();
  }, [state.signUpPaymentType]);

  useEffect(() => {
    if (state.signUpComplete && updateSignUpStatus === "Fulfilled") {
      dispatch(setCurrentFormStep(ROUTES.complete));
      sendAnalyticsPageEvent(
        "Customer Sign Up By Plan",
        details.product,
        details.product,
      );
      navigate(ROUTES.complete);
    } else if (updateSignUpStatus === "Failed") {
      setState((currentState) => ({
        ...currentState,
        errSubmission:
          "Something went wrong, please check your details and try again.",
        signUpComplete: false,
      }));
    }
  }, [navigate, updateSignUpStatus, details.product, state.signUpComplete]);

  useEffect(() => {
    const windcaveTransactionId = fetchWindcaveTransactionId();
    const query = location?.search;
    const redirectFromWindcave =
      query.includes("result=") && query.includes("userid=");

    if (windcaveTransactionId) {
      dispatch(checkSuccessfulAuthorisationAction.request());
    }

    if (redirectFromWindcave || windcaveTransactionId) {
      setState((currentState) => ({
        ...currentState,
        signUpPaymentType: "credit_card",
      }));
    }

    if (
      !state.navigatingToCreditCard &&
      !location.pathname.startsWith("/join")
    ) {
      window.addEventListener("beforeunload", throwAlert);
      return () => {
        window.removeEventListener("beforeunload", throwAlert);
      };
    }
  }, [state.navigatingToCreditCard, location.pathname, location?.search]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const throwAlert = (e: any) => {
    e.preventDefault();
    e.returnValue = "";
  };

  const validateForm = useCallback(
    (form: { checkValidity: () => boolean }) => {
      if (
        state.signUpPaymentType === "credit_card" &&
        windcaveAuthorisationRequestStatus !== REQUEST_STATUS.Fulfilled
      ) {
        setState((currentState) => ({
          ...currentState,
          errCreditCard: "Please add a credit card",
        }));

        return false;
      }

      if (form.checkValidity() === false) {
        setState((currentState) => ({
          ...currentState,
          errSubmission:
            "Looks like we're missing some required information to proceed. Check form fields marked in red above.",
        }));

        return false;
      }

      return true;
    },
    [state, windcaveAuthorisationRequestStatus],
  );

  /**
   * Form only submit via Submit button, on both keyboard navigation and mouse click
   */
  const handleFormSubmit = async () => {
    const form = formRef.current;
    if (!validateForm(form)) {
      return false;
    }
    saveUserInformationLocally();
    dispatch(setFlickSignUpStateComplete());
    dispatch(updateFlickSignUpAction.request());
    sendAnalyticsCustomEvent("join_payment", {
      plan_type: details.product && PRODUCT_LABELS[details.product],
      icp_number: address?.icp,
      address_found: address?.icp ? "yes" : "no",
      eligibility: selectedEligibilityType ? "yes" : "maybe",
      registry_load_type: estimatedUsageType,
      customer_type_system: userChecks.isCommercialAccount
        ? "commercial"
        : "residential",
      self_select_load_group: property.usageType,
      payment_success: "success",
      promo_code: state.promoCode,
      self_select_source: state.leadSource,
    });
    setState((currentState) => ({ ...currentState, signUpComplete: true }));
  };

  /**
   * making sure form validation is called on button submit, both keyboard navigation and click event
   */
  const handleContinuePress = async (
    e: React.KeyboardEvent<HTMLButtonElement>,
  ) => {
    try {
      if (e.key === "Enter") {
        // trigger form default validation
        formRef.current.dispatchEvent(
          new Event("submit", { cancelable: true }),
        );
        await handleFormSubmit();
      }
    } catch (err) {
      // this rollbar error log needs to be monitoring and consider removing in the future
      rollbar.error("Form Signup Step 2 Submit Error", err);
    }
  };

  /**
   * This onKeyDown event is used to prevent the default behavior of the Enter key on the form
   * to prevent premature form validation
   */
  const preventFormSubmitOnEnter = (
    e: React.KeyboardEvent<HTMLFormElement>,
  ) => {
    if (e.key === "Enter") {
      e.preventDefault();
    }
  };

  const saveUserInformationLocally = () => {
    const getApiPaymentType = (): ApiPaymentTypes | undefined => {
      if (!state.signUpPaymentType) return undefined;
      if (state.signUpPaymentType === "credit_card") return "credit_card";

      return "direct_debit";
    };

    let bankAccountType: BankAccountType | undefined;
    if (state.signUpPaymentType === "direct_debit_joint") {
      bankAccountType = "joint_signatory";
    }
    let directDebit;
    if (state.signUpPaymentType === "direct_debit_personal") {
      bankAccountType = "personal";
      directDebit = {
        accountName: state.accName,
        accountNumber: state.accNumber.replace(/[-\s]/g, "").trim(),
        pariculars: state.statementParticulars,
        code: state.statementCode,
        authorityConfirmed: state.authorityCheck,
        tncsAgreed: state.directDebitTnCCheck,
      };
    }
    dispatch(setSignUpPaymentType(state.signUpPaymentType));
    dispatch(setLeadSource(state.leadSource));
    dispatch(setPromoCode(state.promoCode));

    if (getApiPaymentType()) {
      dispatch(
        setUserPayment({
          bankAccountType,
          paymentType: getApiPaymentType(),
          directDebit,
          billingPeriod: state.billingPeriod,
        }),
      );
    }
  };

  const navigateBack = () => {
    saveUserInformationLocally();
    dispatch(setCurrentFormStep(ROUTES.details));
  };

  const handlePaymentChange = useCallback(
    (newSignUpPaymentType: SignUpFormPaymentTypes) => {
      const creditCardPayment = newSignUpPaymentType === "credit_card";
      setState((currentState) => ({
        ...currentState,
        signUpPaymentType: newSignUpPaymentType,
        errPaymentType: "",
        navigatingToCreditCard: creditCardPayment,
      }));
    },
    [],
  );

  const handleBillingPeriodChange = useCallback(
    (billingPeriod: BillingPeriod) => {
      setState((currentState) => ({ ...currentState, billingPeriod }));
      dispatch(updateFlickSignUpAction.request());
    },
    [],
  );

  const handleLeadSourceChange = useCallback(
    (event: React.ChangeEvent<HTMLSelectElement>) => {
      let newLeadSource: string | undefined = event.target.value;
      if (newLeadSource === "") {
        newLeadSource = undefined;
      }

      setState((currentState) => ({
        ...currentState,
        leadSource: newLeadSource,
      }));
    },
    [],
  );

  const handleAccNameChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = event.target;
      const pattern = DD_ACCOUNT_NAME_REGEX_PATTERN;
      if (pattern.test(value) || value === "") {
        setState((currentState) => ({
          ...currentState,
          accName: value,
          errAccName: "",
        }));
      }
    },
    [],
  );

  const handleAccNumberChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = event.target;
      setState((currentState) => ({ ...currentState, accNumber: value }));
    },
    [],
  );

  const handleParticularsChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = event.target;
      setState((currentState) => ({
        ...currentState,
        statementParticulars: value,
      }));
    },
    [],
  );

  const handleCodeChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = event.target;
      setState((currentState) => ({
        ...currentState,
        statementCode: value,
        errStatementCode: "",
      }));
    },
    [],
  );

  const handlePromoCodeChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = event.target;
      const pattern = PROMO_CODE_REGEX_PATTERN;
      if (pattern.test(value) || value === "") {
        setState((currentState) => ({ ...currentState, promoCode: value }));
      }
    },
    [],
  );

  const handleAuthorityCheck = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { checked } = event.target;
      setState((currentState) => ({
        ...currentState,
        authorityCheck: checked,
      }));
    },
    [],
  );

  const handleDirectDebitTnCCheck = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { checked } = event.target;
      setState((currentState) => ({
        ...currentState,
        directDebitTnCCheck: checked,
      }));
    },
    [],
  );

  const handleAddCreditCard = useCallback(() => {
    dispatch(setLeadSource(state.leadSource));
    dispatch(setPromoCode(state.promoCode));
    dispatch(
      setUserPayment({
        paymentType: "credit_card",
        billingPeriod: state.billingPeriod,
      }),
    );
    dispatch(updateFlickSignUpAction.request());
    dispatch(createWindcaveTransactionAction.request());
  }, [state.leadSource, state.promoCode, state.billingPeriod]);

  return (
    <section className="layout page payment">
      <Prompt
        when={
          !(
            location.pathname.startsWith("/join") ||
            state.navigatingToCreditCard
          )
        }
        message={"Leave the page? Changes you have made may not be saved."}
      />
      <ul className="page_indicator_wrapper">
        <li className="page_indicator pointer" onClick={navigateBack}>
          01
        </li>
        <li className="page_indicator page_indicator_active">02</li>
      </ul>
      <h1 className="heading heading_page">Payment details</h1>
      <p>You will not be charged until after you receive your first bill.</p>
      <h2 className="heading heading_subpage heading_subpage--compact">
        Billing frequency
      </h2>
      <br />
      <form
        onSubmit={handleFormSubmit}
        data-validate
        data-synthetic-submit
        onKeyDown={preventFormSubmitOnEnter}
        ref={formRef}
      >
        <fieldset className="form_group form_row">
          <legend>How often would you like to pay?</legend>
          <div className="radio_confirm form_field">
            <input
              type="radio"
              name="billing_period"
              value="weekly"
              checked={state.billingPeriod === "weekly"}
              id="billing_period_weekly"
              onChange={() => handleBillingPeriodChange("weekly")}
              data-msg-value-missing="What's your preferred billing frequency?"
              required
            />
            <label htmlFor="billing_period_weekly">
              <span>Weekly</span>
            </label>
          </div>
          <div className="radio_confirm form_field">
            <input
              type="radio"
              name="billing_period"
              value="fortnightly"
              checked={state.billingPeriod === "fortnightly"}
              id="billing_period_fortnightly"
              onChange={() => handleBillingPeriodChange("fortnightly")}
              data-msg-value-missing="What's your preferred billing frequency?"
              required
            />
            <label htmlFor="billing_period_fortnightly">
              <span>Fortnightly</span>
            </label>
          </div>
          <div className="radio_confirm form_field">
            <input
              type="radio"
              name="billing_period"
              value="monthly"
              checked={state.billingPeriod === "monthly"}
              id="billing_period_monthly"
              onChange={() => handleBillingPeriodChange("monthly")}
              data-msg-value-missing="What's your preferred billing frequency?"
              required
            />
            <label htmlFor="billing_period_monthly">
              <span>Monthly</span>
            </label>
          </div>
        </fieldset>
        <h2 className="heading heading_subpage heading_subpage--compact">
          Preferred payment method
        </h2>
        <br />
        <fieldset className="form_group form_row">
          <legend>Which way would you like to pay?</legend>
          <div className="radio_confirm form_field">
            <input
              type="radio"
              name="payment_type"
              value="direct_debit_personal"
              checked={state.signUpPaymentType === "direct_debit_personal"}
              id="payment_type_direct_debit_personal"
              onChange={() => handlePaymentChange("direct_debit_personal")}
              data-msg-value-missing="Please select a payment method"
              required
            />
            <label htmlFor="payment_type_direct_debit_personal">
              <span>Direct debit from my personal or a joint bank account</span>
            </label>
            {state.signUpPaymentType === "direct_debit_personal" && (
              <div className="form_subform">
                <div className="form_row">
                  <div className="form_field">
                    <div>
                      <input
                        id="direct_debit_account_name"
                        className="form_field_medium"
                        type="text"
                        value={state.accName}
                        onChange={handleAccNameChange}
                        data-msg-value-missing="Whose name is the account under?"
                        required
                      />
                      <label htmlFor="direct_debit_account_name">
                        Account name
                      </label>
                    </div>
                    <p className="form_field_helper">
                      You can only use letters, numbers, full stops, spaces or,
                      @ ? * # : {} ( ) - & ' \\ in the direct debit account
                      name.
                      <br />
                      Commas are not supported.
                    </p>
                  </div>
                </div>
                <div className="form_row">
                  <div className="form_field">
                    <div>
                      <input
                        id="direct_debit_account_number"
                        className="form_field_medium"
                        type="text"
                        value={state.accNumber}
                        onChange={handleAccNumberChange}
                        data-msg-pattern-mismatch="That’s not quite what we were expecting, sorry."
                        pattern="^[0-3][0-9]([- ]?)[0-9]{4}([- ]?)[0-9]{7}([- ]?)(0?)[0-9]{2}$"
                        placeholder="e.g. 12-3456-7890123-000"
                        data-msg-value-missing="We need the account number please."
                        aria-describedby="form_field_helper_account_number"
                        required
                      />
                      <label htmlFor="direct_debit_account_number">
                        Bank account number
                      </label>
                    </div>
                    <p className="form_field_helper">
                      Please make sure the direct debit is linked to an everyday
                      account, not a savings account.
                    </p>
                  </div>
                </div>
                <div className="form_row">
                  <div className="form_field">
                    <div>
                      <input
                        id="direct_debit_particulars"
                        className="form_field_medium"
                        type="text"
                        pattern={ALPHANUMERIC_PATTERN}
                        maxLength={12}
                        data-msg-too-long="Please keep the particulars under 12 characters."
                        data-msg-pattern-mismatch="You can only use letters or numbers."
                        value={state.statementParticulars}
                        onChange={handleParticularsChange}
                      />
                      <label htmlFor="direct_debit_particulars">
                        Particulars
                        <span className="label_optional"> (Optional)</span>
                      </label>
                    </div>
                  </div>
                </div>
                <div className="form_row">
                  <div className="form_field">
                    <div>
                      <input
                        id="direct_debit_code"
                        className="form_field_medium"
                        type="text"
                        pattern={ALPHANUMERIC_PATTERN}
                        maxLength={12}
                        data-msg-pattern-mismatch="You can only use letters or numbers."
                        data-msg-too-long="Please keep the code to under 12 characters."
                        value={state.statementCode}
                        onChange={handleCodeChange}
                      />
                      <label htmlFor="direct_debit_code">
                        Code
                        <span className="label_optional"> (Optional)</span>
                      </label>
                    </div>
                  </div>
                </div>
                <div className="checkbox_confirm form_row">
                  <input
                    type="checkbox"
                    value="authority_check_checkbox"
                    checked={state.authorityCheck}
                    onChange={handleAuthorityCheck}
                    id="checkbox_authority_check"
                    data-msg-value-missing="We kinda need you to accept these conditions before we can continue."
                    required
                  />
                  <label htmlFor="checkbox_authority_check">
                    <span>
                      I confirm I have authority over this bank account
                    </span>
                  </label>
                </div>
                <div className="checkbox_confirm form_row">
                  <input
                    type="checkbox"
                    value="credit_check_checkbox"
                    checked={state.directDebitTnCCheck}
                    onChange={handleDirectDebitTnCCheck}
                    id="checkbox_credit_check"
                    data-msg-value-missing="We kinda need you to accept these conditions before we can continue."
                    required
                  />
                  <label htmlFor="checkbox_credit_check">
                    <span>
                      I have read and accept the direct debit{" "}
                      <a
                        href="https://www.flickelectric.co.nz/direct-debit-terms"
                        className="link link_strong"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Terms &amp; Conditions
                      </a>
                    </span>
                  </label>
                </div>
              </div>
            )}
          </div>
          <div className="radio_confirm form_field">
            <input
              type="radio"
              name="payment_type"
              value="direct_debit_joint"
              checked={state.signUpPaymentType === "direct_debit_joint"}
              id="payment_type_direct_debit_joint"
              onChange={() => handlePaymentChange("direct_debit_joint")}
              data-msg-value-missing="Please select a payment method"
              required
            />
            <label htmlFor="payment_type_direct_debit_joint">
              <span>
                Direct debit from a bank account that requires 2 or more people
                to approve/sign
              </span>
            </label>
            {state.signUpPaymentType === "direct_debit_joint" && (
              <div className="form_subform">
                <div className="form_field">
                  <div>
                    <div className="notification_banner notification_notice icon">
                      <p>
                        You will only need to complete the direct debit form
                        below if transactions from this account require two or
                        more signatures or you are signing up for a business.
                      </p>
                      <a
                        href={process.env.REACT_APP_DIRECT_DEBIT_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="link"
                      >
                        Download a direct debit form
                      </a>
                    </div>
                    <p className="form_field_helper">
                      Please make sure the direct debit is linked to an everyday
                      account, not a savings account.
                    </p>
                    <p className="form_field_helper">
                      We won’t be able to get your application under way until
                      we receive your direct debit form. If you want to fast
                      track your application, you can always&nbsp;
                      <span
                        onClick={() => handlePaymentChange("credit_card")}
                        className="link helper-link"
                      >
                        add a credit card or debit card
                      </span>
                      &nbsp;instead.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
          <div className="radio_confirm form_field">
            <input
              type="radio"
              name="payment_type"
              value="credit_card"
              checked={state.signUpPaymentType === "credit_card"}
              id="payment_type_credit_card"
              onChange={() => handlePaymentChange("credit_card")}
              data-msg-value-missing="Please select a payment method"
              required
            />
            <label htmlFor="payment_type_credit_card">
              <span>Credit card or debit card</span>
            </label>
            {state.signUpPaymentType === "credit_card" && (
              <div className="form_subform">
                <div className="form_field">
                  {windcaveAuthorisationRequestStatus ===
                  REQUEST_STATUS.Fulfilled ? (
                    <div>
                      <p className="info_text info_text_success">
                        Card succesfully added
                      </p>
                      <div className="formatter_payment_card">
                        <p>
                          <s aria-hidden="true">XXXX XXXX XXXX XXXX</s>
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <button
                        type="button"
                        className="button"
                        onClick={handleAddCreditCard}
                      >
                        Add credit or debit card
                      </button>
                      <p>
                        Make sure you come back here once you’ve finished adding
                        your credit or debit card details.
                      </p>
                    </div>
                  )}
                  <div className="form_field_helper error">
                    {state.errCreditCard && state.errCreditCard}
                  </div>
                </div>
              </div>
            )}
          </div>
        </fieldset>
        <h2 className="heading heading_subpage heading_subpage--compact">
          Before we wrap up
        </h2>
        <br />
        <fieldset className="form_group form_row no_bottom_margin">
          <legend>Do you have a promo code? (optional)</legend>
          <div className="form_field">
            <div>
              <input
                id="promo_code"
                type="text"
                className="form_field_medium"
                value={state.promoCode}
                onChange={handlePromoCodeChange}
              />
              <label htmlFor="promo_code">Neat. What is it?</label>
            </div>
            <p className="form_field_helper">
              Our Friend Get Friend process has changed. Please head to our{" "}
              <a
                href="https://www.flickelectric.co.nz/friend-get-friend/"
                target="_blank"
                className="link helper-link"
              >
                website
              </a>{" "}
              for the updated way to get in on Friend Get Friend.
            </p>
          </div>
          {error?.includes("promotional code") && (
            <div className="form_field_helper error">{error}</div>
          )}
        </fieldset>
        <fieldset className="form_group form_row no_bottom_margin">
          <div className="form_field form_field_medium">
            <label className="heading heading_section" htmlFor="lead_source">
              Where did you hear about Flick? (optional)
            </label>
            <br />
            <br />
            <div>
              <select
                id="lead_source"
                name="lead_source"
                defaultValue={state.leadSource}
                onChange={handleLeadSourceChange}
                className="input_unlabeled"
              >
                <option className="initial" value="">
                  Select one
                </option>
                <option value="web_search">Web Search</option>
                <option value="friend_or_family">Friend Or Family</option>
                <option value="social_media">Social Media</option>
                <option value="advertising">Advertising</option>
                <option value="news_story">News Story</option>
                <option value="comparison_site">Comparison Site</option>
                <option value="i'm_a_previous_customer">
                  I'm A Previous Customer
                </option>
                <option value="the_spinoff">The Spinoff</option>
                <option value="other">Other</option>
              </select>
            </div>
          </div>
        </fieldset>
        <br />
        <br />
        <br />
        <div className="form_row">
          <div className="form_actions form_field space-between">
            <button
              type="submit"
              className="button button_primary"
              id="button-next-step-details"
              onKeyDown={handleContinuePress}
            >
              Send application
            </button>
            <Link
              className="button button_secondary"
              onClick={navigateBack}
              to={ROUTES.details}
              discover="none"
            >
              Back
            </Link>
          </div>
        </div>
        {state.errSubmission && (
          <div className="form_field_helper error">{state.errSubmission}</div>
        )}
      </form>
    </section>
  );
}
